import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import {
  MatDialogActions,
  MatDialogClose,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle,
} from '@angular/material/dialog';
import { MatError, MatFormField } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import {
  catchError,
  filter,
  map,
  merge,
  shareReplay,
  take,
  takeUntil,
  tap,
  throwError,
  timer,
} from 'rxjs';
import { BaseDialog } from '../../../../components/dialog/base-dialog';
import { ConfirmHandler } from '../../../../components/dialog/dialog.model';
import { PhoneInputComponent } from '../../../../components/phone-input/phone-input.component';
import { phoneValidator } from '../../../../components/phone-input/phone-input.validators';
import { ContactResponseModel } from '../../../../models/contacts.model';

export interface SaveContactDialogResult {
  name: string;
  phoneNumber: string;
  email: string;
}

export interface SaveContactDialogData {
  title: string;
  contactData?: ContactResponseModel;
  onConfirm: ConfirmHandler<SaveContactDialogResult>;
}

@Component({
  selector: 'app-save-contact-dialog',
  imports: [
    MatButton,
    MatDialogActions,
    MatDialogClose,
    MatDialogContent,
    MatDialogTitle,
    MatError,
    MatFormField,
    MatInput,
    MatProgressSpinner,
    PhoneInputComponent,
    ReactiveFormsModule,
  ],
  templateUrl: './save-contact-dialog.component.html',
  styleUrl: './save-contact-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SaveContactDialogComponent extends BaseDialog<SaveContactDialogData> {
  private readonly _dialogRef =
    inject<MatDialogRef<SaveContactDialogComponent>>(MatDialogRef);
  private readonly _fb = inject(FormBuilder);

  protected readonly form = this._fb.group({
    name: this._fb.control('', [Validators.required, Validators.maxLength(100)]),
    phoneNumber: this._fb.control('', [Validators.required, phoneValidator]),
    email: this._fb.control('', [Validators.email]),
  });

  protected readonly submitted = signal(false);
  protected readonly loading = signal(false);

  protected get controls() {
    return this.form.controls;
  }

  constructor() {
    super();

    if (this.data.contactData) {
      this.form.patchValue(this.data.contactData);
    }
  }

  override handleConfirm(): void {
    if (this.form.invalid) {
      return;
    }

    const formValue = this.form.value;

    const data = {
      ...formValue,
      email: formValue.email === '' ? null : formValue.email,
    } as SaveContactDialogResult;

    const callback$ = this.data.onConfirm(data);

    const source$ = callback$.pipe(
      shareReplay(1),
      catchError((error) => {
        this.submitted.set(false);

        return throwError(() => error);
      })
    );

    const loading$ = merge(
      timer(1000).pipe(
        map(() => true),
        takeUntil(source$)
      ),

      source$.pipe(map(() => false))
    );

    loading$
      .pipe(
        tap((state) => this.loading.set(state)),
        filter((state) => !state),
        tap(() => this._dialogRef.close(true)),
        take(1)
      )
      .subscribe();
  }
}
